// =================================================================
// FilesSidebar.js - Système Fichiers Intelligent Oracle TRYA
// Adapté du code existant + conformité mockup V2 + Sélecteur Destination
// =================================================================

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, Upload, FileText, Image, BarChart3, Trash2, Eye, Edit3, Clock, FileCheck, RefreshCw, FolderOpen, FolderInput, Archive, Share2, X, Download } from 'lucide-react';
import FileActionButtons from './FileActions';
import FilePreviewModal from './FilePreviewModal';
import FolderTree from './FolderTree';
import DocCategoryChip from '../family/DocCategoryChip';
import { getFamilyGraph } from '../../services/familyApi';
import { toast, confirm as confirmModal } from '../ui/Toast';

const FilesSidebar = ({ projectId, user, refreshKey, onFileAnalyze, onBatchAnalyze, pasteWidget }) => {
console.log('FilesSidebar - projectId reçu:', projectId);
  const [previewFile, setPreviewFile] = useState(null);
  const [permanentFiles, setPermanentFiles] = useState([]);
  const [sessionFiles, setSessionFiles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [uploadZoneActive, setUploadZoneActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [accessError, setAccessError] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [selectedFileIds, setSelectedFileIds] = useState(new Set());
  const [selectMode, setSelectMode] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadingNames, setUploadingNames] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [folderRefreshKey, setFolderRefreshKey] = useState(0);
  const [currentBreadcrumb, setCurrentBreadcrumb] = useState([]);
  const [moveFileId, setMoveFileId] = useState(null); // file being moved
  const [moveMenuPos, setMoveMenuPos] = useState(null); // { top, left } for fixed menu
  const [foldersList, setFoldersList] = useState([]); // flat list for move menu

  // Rename fichier (Session E — P-FILE-ACTIONS)
  const [renameFileId, setRenameFileId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);

  // Share cross-projet (Session 31 — réservé admin_general + oracle_fondateur)
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareFileIds, setShareFileIds] = useState([]);
  const [shareTargetProject, setShareTargetProject] = useState('');
  const [shareTargetFolder, setShareTargetFolder] = useState('');
  const [shareProjects, setShareProjects] = useState([]);
  const [shareTargetFolders, setShareTargetFolders] = useState([]);
  const [sharing, setSharing] = useState(false);
  const canShare = ['admin_general', 'oracle_fondateur'].includes(user?.role);
  const [zipImporting, setZipImporting] = useState(false);
  const [zipProgress, setZipProgress] = useState(null); // { current, total, filename, label }
  const [exportingZip, setExportingZip] = useState(false);

  // Stats projet pour section informations supplémentaires
  const [stats, setStats] = useState({
    totalFiles: 0,
    usedSpace: '0 KB',
    totalSpace: '—',
    usagePercentage: 0,
    lastSync: 'Jamais'
  });
  // Session 36 — quota stockage dynamique (via /api/projects/:id/storage)
  const [storageInfo, setStorageInfo] = useState(null);

  // Family P2c — mapping file_id → { category, nationality_tag } pour afficher le chip
  const [familyRoutingByFile, setFamilyRoutingByFile] = useState({});

  // Family P2c — filtre contextuel : quand l'onglet projet actif est 'family',
  // on filtre la sidebar pour ne montrer que les fichiers routés Famille.
  // L'utilisateur peut désactiver le filtre via le bandeau pour revoir tous les fichiers.
  const [projectActiveTab, setProjectActiveTab] = useState(null);
  const [familyFilterDisabled, setFamilyFilterDisabled] = useState(false);
  useEffect(() => {
    const onTab = (e) => {
      if (e.detail && e.detail.projectId === projectId) {
        setProjectActiveTab(e.detail.tab);
        setFamilyFilterDisabled(false); // réinitialise quand l'onglet change
      }
    };
    window.addEventListener('itriia-project-tab', onTab);
    return () => window.removeEventListener('itriia-project-tab', onTab);
  }, [projectId]);
  const familyFilterActive = projectActiveTab === 'family' && !familyFilterDisabled;
  useEffect(() => {
    if (!projectId) { setFamilyRoutingByFile({}); return; }
    const flagOn = window.__itriiaFeatureFlags?.family_module === true;
    if (!flagOn) { setFamilyRoutingByFile({}); return; }
    let cancelled = false;
    getFamilyGraph(projectId, { include: 'doc_categories,document_routing' })
      .then((g) => {
        if (cancelled) return;
        const catById = Object.fromEntries((g?.doc_categories || []).map((c) => [c.id, c]));
        const map = {};
        (g?.document_routing || []).forEach((r) => {
          if (r.file_id && r.category_id && catById[r.category_id]) {
            map[r.file_id] = { category: catById[r.category_id], nationality_tag: r.nationality_tag };
          }
        });
        setFamilyRoutingByFile(map);
      })
      .catch(() => { if (!cancelled) setFamilyRoutingByFile({}); });
    return () => { cancelled = true; };
  }, [projectId, refreshKey]);

  // Fonctions utilitaires pour les statistiques
  const calculateTotalSize = (files) => {
    if (!files || files.length === 0) return '0 KB';
    
    const totalBytes = files.reduce((total, file) => {
      const sizeBytes = file.file_size ? parseInt(file.file_size) : (file.size ? parseInt(file.size) : 0);
      return total + sizeBytes;
    }, 0);

    if (totalBytes < 1024) return `${totalBytes} octets`;
    if (totalBytes < 1024 * 1024) return `${Math.round(totalBytes / 1024)} KB`;
    return `${(totalBytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getLastUploadDate = (files) => {
    if (!files || files.length === 0) return 'Jamais';
    
    const mostRecent = files.reduce((latest, file) => {
      const fileDate = new Date(file.uploaded_at);
      return fileDate > new Date(latest.uploaded_at) ? file : latest;
    });

    const uploadDate = new Date(mostRecent.uploaded_at);
    const now = new Date();
    const diffMs = now - uploadDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Maintenant';
    if (diffMins < 60) return `Il y a ${diffMins}min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return uploadDate.toLocaleDateString('fr-FR');
  };

  const calculateUsagePercentage = (usedBytes, maxBytes) => {
    if (!maxBytes || maxBytes <= 0) return 0;
    return Math.min(Math.round((usedBytes / maxBytes) * 100), 100);
  };

  // Fermer le menu move quand on clique ailleurs
  useEffect(() => {
    const handler = () => setMoveFileId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  // Chargement initial + re-fetch quand folder change
  useEffect(() => {
    loadFilesData();
  }, [projectId, refreshKey, currentFolderId]);

  // S59 P3 — quand le parent bump refreshKey (apply organisation, upload),
  // recharge aussi l'arbre des dossiers (FolderTree) qui a sa propre clé interne.
  useEffect(() => {
    if (refreshKey === undefined || refreshKey === null) return;
    setFolderRefreshKey(k => k + 1);
  }, [refreshKey]);

  const loadFilesData = async () => {
    setLoading(true);
    setAccessError(null);

    // Tentative chargement API - MÉTHODE IDENTIQUE À FILESINTERFACE
    if (projectId) {
      try {
        const token = localStorage.getItem('oracle_token');
        // S59 P3 fix : à la racine, passer folder_id=root sinon le backend renvoie TOUS les fichiers du projet (pas filtré).
        const folderParam = currentFolderId !== null ? `&folder_id=${currentFolderId}` : '&folder_id=root';

        // Session 36 — fetch storage quota en parallèle (authoritative)
        let storage = null;
        try {
          const sres = await axios.get(`/api/projects/${projectId}/storage`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (sres.data?.success) {
            storage = sres.data;
            setStorageInfo(storage);
          }
        } catch (e) { /* silencieux — fallback sans quota */ }

        const response = await axios.get(`/api/projects/${projectId}/files?page=1&limit=50${folderParam}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data && response.data.files) {
          console.log('FilesSidebar - Données API reçues:', response.data);

          // Transformation selon la méthode FilesInterface.js EXACTE
          const adaptedFiles = response.data.files.map(file => ({
            id: file.id,
            name: file.original_name,
            size: file.file_size ?
              (parseInt(file.file_size) < 1024 ?
                `${file.file_size} octets` :
                `${Math.round(parseInt(file.file_size) / 1024)} KB`) :
              (file.size ?
                (parseInt(file.size) < 1024 ?
                  `${file.size} octets` :
                  `${Math.round(parseInt(file.size) / 1024)} KB`) :
                'N/A'),
            age: (() => {
              const uploadDate = new Date(file.uploaded_at);
              const now = new Date();
              const diffMs = now - uploadDate;
              const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
              const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
              const diffMins = Math.floor(diffMs / (1000 * 60));

              if (diffMins < 1) return 'maintenant';
              if (diffMins < 60) return `${diffMins}min`;
              if (diffHours < 24) return `${diffHours}h`;
              if (diffDays < 7) return `${diffDays}j`;
              return `${Math.floor(diffDays / 7)}sem`;
            })(),
            type: file.mimetype,
            icon: (() => {
              if (file.mimetype && file.mimetype.includes('pdf')) return '📄';
              if (file.mimetype && file.mimetype.includes('image')) return '🖼️';
              if (file.mimetype && file.mimetype.includes('text')) return '📝';
              if (file.mimetype && file.mimetype.includes('javascript')) return '⚡';
              if (file.mimetype && file.mimetype.includes('excel') || file.mimetype && file.mimetype.includes('spreadsheet')) return '📊';
              if (file.mimetype && file.mimetype.includes('word') || file.mimetype && file.mimetype.includes('document')) return '📃';
              return '📄';
            })(),
            tags: (() => {
              const tags = [];
              if (file.storage_status === 'nextcloud') tags.push('Synchro');
              if (file.upload_metadata?.shared_from) tags.push('Partagé');
              if (tags.length === 0) tags.push('Standard');
              return tags;
            })(),
            actions: ['view', 'delete'],
            storage: file.storage_status === 'nextcloud' ? 'nextcloud' : 'local',
            upload_date: new Date(file.uploaded_at).toLocaleDateString('fr-FR'),
            isShared: !!(file.upload_metadata?.shared_from),
            // Upload async — badge de traitement en arrière-plan
            processing_status: file.processing_status,
            status: file.processing_status === 'processing'
              ? 'Traitement…'
              : (file.processing_status === 'error' ? 'Échec' : undefined)
          }));

          setPermanentFiles(adaptedFiles);

          // Charger les fichiers de la session active
          loadSessionFiles();

          // Calcul des vraies statistiques
          const totalBytes = response.data.files.reduce((total, file) => {
            const sizeBytes = file.file_size ? parseInt(file.file_size) : (file.size ? parseInt(file.size) : 0);
            return total + sizeBytes;
          }, 0);

          // Session 36 — utilise le quota backend (authoritative) quand disponible
          const authoritativeUsed = storage?.used ?? totalBytes;
          const authoritativeUsedHuman = storage?.used_human || calculateTotalSize(response.data.files);
          const authoritativeQuotaHuman = storage?.quota_human || '—';
          const authoritativePercent = storage?.percent ?? calculateUsagePercentage(totalBytes, storage?.quota);

          setStats({
            totalFiles: response.data.files?.length || 0,
            usedSpace: authoritativeUsedHuman,
            totalSpace: authoritativeQuotaHuman,
            usagePercentage: authoritativePercent,
            lastSync: getLastUploadDate(response.data.files)
          });
        }
      } catch (error) {
        console.error('FilesSidebar - Erreur chargement fichiers projets:', error);
        if (error.response?.status === 403) setAccessError(error.response?.data?.message || 'Accès insuffisant à ce projet.');
        setPermanentFiles([]);
        setSessionFiles([]);
      } finally {
        setLoading(false);
      }
    } else {
      setPermanentFiles([]);
      setSessionFiles([]);
      setLoading(false);
    }
  };

  const getTagColor = (tag) => {
    switch (tag) {
      case 'Expertise': return '#7c3aed';
      case 'OCR': return '#7c3aed';
      case 'Indexé': return '#16a34a';
      case 'Auto-généré': return '#9333ea';
      case 'Synchro': return '#059669';
      case 'Partagé': return '#8b5cf6';
      case 'Session': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'view': return <Eye size={14} />;
      case 'edit': return <Edit3 size={14} />;
      case 'analyze': return <BarChart3 size={14} />;
      case 'expertise': return <FileCheck size={14} />;
      case 'delete': return <Trash2 size={14} />;
      default: return <FileText size={14} />;
    }
  };

  // Helper function pour détecter le type de fichier
  const getFileCategory = (mimetype) => {
    if (!mimetype) return 'other';
    if (mimetype.includes('pdf')) return 'pdf';
    if (mimetype.includes('image')) return 'image';
    if (mimetype.includes('word') || mimetype.includes('document')) return 'document';
    if (mimetype.includes('javascript') || mimetype.includes('text')) return 'code';
    if (mimetype.includes('spreadsheet') || mimetype.includes('excel')) return 'spreadsheet';
    return 'other';
  };

  // Filtrage fichiers (search + type + filtre Famille contextuel)
  const filterFiles = (files) => {
    return files.filter(file => {
      const matchesSearch = file.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesFilter = filterType === 'all' ||
                           (filterType === 'expertise' && file.tags?.includes('Expertise')) ||
                           (filterType === 'cloud' && file.storage === 'nextcloud') ||
                           (filterType === 'types' && getFileCategory(file.type) !== 'other');
      const matchesFamily = !familyFilterActive || !!familyRoutingByFile[file.id];
      return matchesSearch && matchesFilter && matchesFamily;
    });
  };

  const isRecent = (age) => {
    return age.includes('min') || age.includes('maintenant') || age === '1j';
  };

  // Multi-select helpers
  const toggleFileSelect = (fileId) => {
    setSelectedFileIds(prev => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
  };

  const selectAll = () => {
    const allIds = [...filterFiles(permanentFiles), ...filterFiles(sessionFiles)].map(f => f.id);
    setSelectedFileIds(new Set(allIds));
  };

  const deselectAll = () => setSelectedFileIds(new Set());

  const handleBatchAnalyze = () => {
    if (selectedFileIds.size === 0) return;
    const allFiles = [...permanentFiles, ...sessionFiles];
    const selected = allFiles.filter(f => selectedFileIds.has(f.id));
    // Lancer analyse séquentielle sur chaque fichier sélectionné
    selected.forEach(f => {
      if (onFileAnalyze) onFileAnalyze(f.id, f.name, 'analyze');
    });
    setSelectedFileIds(new Set());
    setSelectMode(false);
  };

  // ── Share cross-projet (Session 31) ───────────────
  const openShareModal = (fileIds) => {
    if (!canShare) return;
    setShareFileIds(fileIds);
    setShareTargetProject('');
    setShareTargetFolder('');
    setShareTargetFolders([]);
    setShareModalOpen(true);
    // Charger la liste des projets
    const token = localStorage.getItem('oracle_token');
    axios.get('/api/projects', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const projects = (res.data || []).filter(p => p.id !== parseInt(projectId));
        setShareProjects(projects);
      }).catch(() => {});
  };

  const loadShareTargetFolders = async (targetProjId) => {
    if (!targetProjId) { setShareTargetFolders([]); return; }
    try {
      const token = localStorage.getItem('oracle_token');
      const res = await axios.get(`/api/projects/${targetProjId}/folders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        const flatten = (nodes, depth = 0) => {
          let result = [];
          for (const n of nodes) {
            result.push({ id: n.id, name: '\u00A0\u00A0'.repeat(depth) + n.name });
            if (n.children) result.push(...flatten(n.children, depth + 1));
          }
          return result;
        };
        setShareTargetFolders(flatten(res.data.tree || []));
      }
    } catch (e) { setShareTargetFolders([]); }
  };

  const handleShare = async () => {
    if (!shareTargetProject || shareFileIds.length === 0 || sharing) return;
    setSharing(true);
    try {
      const token = localStorage.getItem('oracle_token');
      const res = await axios.post(`/api/projects/${projectId}/files/share-to-project`, {
        file_ids: shareFileIds,
        target_project_id: parseInt(shareTargetProject, 10),
        target_folder_id: shareTargetFolder || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success) {
        toast.success(`${res.data.shared_count} fichier(s) partagé(s) vers "${res.data.target_project}"`);
        setShareModalOpen(false);
        setSelectedFileIds(new Set());
        setSelectMode(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || e.response?.data?.error || 'Erreur partage');
    } finally {
      setSharing(false);
    }
  };

  // Télécharger tous les fichiers du projet en une archive .zip
  const handleExportZip = async () => {
    if (!projectId || exportingZip) return;
    setExportingZip(true);
    try {
      const token = localStorage.getItem('oracle_token');
      const res = await axios.get(`/api/projects/${projectId}/files/export.zip`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      // Nom d'archive depuis Content-Disposition, fallback générique
      const disp = res.headers['content-disposition'] || '';
      const match = disp.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : `projet-${projectId}-fichiers.zip`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Archive téléchargée');
    } catch (e) {
      // La réponse d'erreur est un blob → tenter de lire le message JSON
      let msg = 'Erreur lors de la création de l\'archive';
      if (e.response?.status === 404) {
        msg = 'Aucun fichier à exporter pour ce projet';
      } else if (e.response?.data instanceof Blob) {
        try { msg = JSON.parse(await e.response.data.text()).error || msg; } catch (_) {}
      }
      toast.error(msg);
    } finally {
      setExportingZip(false);
    }
  };

  // Charger les fichiers de la session active
  const loadSessionFiles = async () => {
    if (!projectId) return;
    try {
      const token = localStorage.getItem('oracle_token');
      const response = await axios.get(`/api/projects/${projectId}/sessions/active/files`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data?.success && response.data.files) {
        const adapted = response.data.files.map(file => ({
          id: file.id,
          name: file.original_name || file.filename,
          size: file.file_size ?
            (parseInt(file.file_size) < 1024 ? `${file.file_size} octets` : `${Math.round(parseInt(file.file_size) / 1024)} KB`) : 'N/A',
          age: (() => {
            const d = new Date(file.uploaded_at);
            const diff = Date.now() - d.getTime();
            const mins = Math.floor(diff / 60000);
            if (mins < 1) return 'maintenant';
            if (mins < 60) return `${mins}min`;
            const hrs = Math.floor(mins / 60);
            if (hrs < 24) return `${hrs}h`;
            return `${Math.floor(hrs / 24)}j`;
          })(),
          type: file.mimetype,
          icon: (() => {
            if (file.mimetype?.includes('pdf')) return '📄';
            if (file.mimetype?.includes('image')) return '🖼️';
            if (file.mimetype?.includes('text')) return '📝';
            if (file.mimetype?.includes('javascript')) return '⚡';
            return '📄';
          })(),
          tags: ['Session'],
          actions: ['view', 'delete']
        }));
        setSessionFiles(adapted);
      } else {
        setSessionFiles([]);
      }
    } catch (error) {
      console.error('FilesSidebar - Erreur session files:', error);
      setSessionFiles([]);
    }
  };

  // Supprimer un fichier (soft delete)
  const handleDeleteFile = async (fileId) => {
    if (!await confirmModal('Supprimer ce fichier ?', { danger: true, confirmText: 'Supprimer' })) return;
    try {
      const token = localStorage.getItem('oracle_token');
      await axios.delete(`/api/projects/${projectId}/files/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFolderRefreshKey(k => k + 1);
      await loadFilesData();
    } catch (error) {
      console.error('Erreur suppression:', error);
      toast.error('Erreur suppression: ' + (error.response?.data?.message || error.response?.data?.error || error.message));
    }
  };

  // Renommer un fichier (Session E — P-FILE-ACTIONS)
  const openRenameModal = (file) => {
    setRenameFileId(file.id);
    setRenameValue(file.name || file.original_name || '');
  };

  const handleRenameFile = async () => {
    if (!renameFileId || !renameValue.trim() || renaming) return;
    setRenaming(true);
    try {
      const token = localStorage.getItem('oracle_token');
      await axios.put(`/api/projects/${projectId}/files/${renameFileId}/rename`, {
        name: renameValue.trim()
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Fichier renommé');
      setRenameFileId(null);
      setRenameValue('');
      setFolderRefreshKey(k => k + 1);
      await loadFilesData();
    } catch (e) {
      toast.error(e.response?.data?.message || e.response?.data?.error || 'Erreur renommage');
    } finally {
      setRenaming(false);
    }
  };

  // Déplacer un fichier vers un dossier
  const handleMoveFile = async (fileId, targetFolderId) => {
    try {
      const token = localStorage.getItem('oracle_token');
      await axios.put(`/api/projects/${projectId}/files/${fileId}/move`, {
        folder_id: targetFolderId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(targetFolderId ? 'Fichier déplacé dans le dossier' : 'Fichier remis à la racine');
      setMoveFileId(null);
      setFolderRefreshKey(k => k + 1);
      await loadFilesData();
    } catch (e) {
      toast.error(e.response?.data?.message || e.response?.data?.error || 'Erreur déplacement');
    }
  };

  // Charger la liste de dossiers pour le menu move
  const openMoveMenu = async (fileId, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setMoveMenuPos({ top: rect.bottom + 4, left: Math.max(rect.right - 200, 8) });
    setMoveFileId(fileId);
    try {
      const token = localStorage.getItem('oracle_token');
      const res = await axios.get(`/api/projects/${projectId}/folders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        // Flatten tree for simple list
        const flat = [];
        const flatten = (nodes, prefix = '') => {
          for (const n of nodes) {
            flat.push({ id: n.id, name: n.name, path: n.path, displayName: prefix + n.name });
            if (n.children) flatten(n.children, prefix + '  ');
          }
        };
        flatten(res.data.tree || []);
        setFoldersList(flat);
      }
    } catch (e) {
      setFoldersList([]);
    }
  };

  // Import ZIP structuré avec SSE progression
  const handleZipImport = async (file) => {
    if (!file || !projectId) return;

    setZipImporting(true);
    setZipProgress({ current: 0, total: 0, filename: file.name, label: 'Envoi du ZIP...' });

    try {
      const token = localStorage.getItem('oracle_token');
      const formData = new FormData();
      formData.append('zipfile', file);
      if (currentFolderId !== null) {
        formData.append('parent_folder_id', currentFolderId);
      }

      // SSE via fetch (pas axios — on a besoin du stream)
      const response = await fetch(`/api/projects/${projectId}/upload/zip`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // garder le fragment incomplet

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));

            if (data.type === 'progress') {
              setZipProgress({
                current: data.current,
                total: data.total,
                filename: data.filename,
                label: data.label
              });
            } else if (data.type === 'status') {
              setZipProgress(prev => ({ ...prev, label: data.label }));
            } else if (data.type === 'warning') {
              toast.warning(data.message);
            } else if (data.type === 'error') {
              toast.error(data.error);
            } else if (data.type === 'done') {
              toast.success(
                `Import terminé : ${data.files_imported} fichier(s), ${data.folders_created} dossier(s)` +
                (data.files_errors > 0 ? ` (${data.files_errors} erreur(s))` : '')
              );
            }
          } catch (e) { /* parse error, ignore */ }
        }
      }

      setFolderRefreshKey(k => k + 1);
      await loadFilesData();

    } catch (error) {
      console.error('[ZIP] Erreur import:', error);
      toast.error('Erreur import ZIP: ' + (error.message || 'Erreur réseau'));
    } finally {
      setZipImporting(false);
      setZipProgress(null);
    }
  };

  // Upload async — relance le chargement tant que des fichiers sont "processing"
  const pollProcessing = async (attempt = 0) => {
    if (attempt >= 20 || !projectId) return; // ~60s max (20 × 3s)
    await new Promise(r => setTimeout(r, 3000));
    try {
      const token = localStorage.getItem('oracle_token');
      const folderParam = currentFolderId !== null ? `&folder_id=${currentFolderId}` : '&folder_id=root';
      const res = await axios.get(`/api/projects/${projectId}/files?page=1&limit=50${folderParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const stillProcessing = (res.data?.files || []).some(f => f.processing_status === 'processing');
      await loadFilesData();
      if (stillProcessing) pollProcessing(attempt + 1);
    } catch (e) { /* stop polling silencieusement */ }
  };

  // Handle upload avec destination — appel API réel
  const handleUpload = async (files) => {
    if (!files.length || !projectId) return;

    setIsUploading(true);
    setUploadingNames(Array.from(files).map(f => f.name));
    try {
      const token = localStorage.getItem('oracle_token');
      const formData = new FormData();
      for (const file of files) {
        formData.append('files', file);
      }
      if (currentFolderId !== null) {
        formData.append('folder_id', currentFolderId);
      }

      const response = await axios.post(`/api/projects/${projectId}/upload/files`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        console.log(`Upload OK: ${response.data.files_processed} fichier(s) reçu(s)`);
        setFolderRefreshKey(k => k + 1);
        await loadFilesData();
        if (response.data.async) {
          toast.info(`${response.data.files_processed} fichier(s) reçu(s) — extraction en cours en arrière-plan`);
          pollProcessing();
        }
      } else {
        console.error('Erreur upload:', response.data.error);
        toast.error('Erreur upload: ' + (response.data.error || 'Erreur inconnue'));
      }
    } catch (error) {
      console.error('Erreur upload:', error);
      toast.error('Erreur upload: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsUploading(false);
      setUploadingNames([]);
    }
  };

  return (
    <div className="files-sidebar">
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { animation: spin 0.8s linear infinite; }
        .files-sidebar {
          display: flex;
          flex-direction: column;
          height: 100%;
          background: var(--oracle-bg-secondary);
        }

        .files-header {
          padding: 10px 16px 8px;
          border-bottom: 1px solid var(--oracle-border);
          background: var(--card-bg);
        }

        .files-title {
          font-size: 14px;
          font-weight: 600;
          color: var(--oracle-text-primary);
          margin-bottom: 8px;
        }

        .files-subtitle {
          font-size: 11px;
          color: var(--text-secondary);
          margin-bottom: 8px;
        }

        .search-box {
          position: relative;
          margin-bottom: 6px;
        }

        .search-input {
          width: 100%;
          padding: 6px 10px 6px 30px;
          border: 1px solid var(--oracle-border);
          border-radius: 6px;
          font-size: 11px;
          background: var(--input-bg);
          color: var(--text);
          transition: all 0.2s ease;
        }

        .search-input:focus {
          outline: none;
          border-color: var(--oracle-red);
          box-shadow: 0 0 0 1px rgba(72, 136, 224, 0.1);
          background: var(--card-bg);
        }

        .search-icon {
          position: absolute;
          left: 10px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--oracle-text-muted);
        }

        .filter-buttons {
          display: flex;
          gap: 4px;
          flex-wrap: wrap;
        }

        .filter-btn {
          padding: 4px 8px;
          background: var(--input-bg);
          border: 1px solid var(--card-border);
          border-radius: 4px;
          font-size: 10px;
          color: var(--text-secondary);
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .filter-btn.active {
          background: #7c3aed;
          color: white;
          border-color: #7c3aed;
        }

        .filter-btn:hover {
          background: var(--card-hover);
        }

        .filter-btn.active:hover {
          background: #6d28d9;
        }

        /* NOUVEAU : Styles sélecteur destination harmonisés */
        .destination-selector {
          margin: 12px 16px 8px;
          background: var(--card-bg);
          border: 1px solid var(--oracle-border);
          border-radius: 8px;
          padding: 12px;
          transition: all 0.2s ease;
        }

        .destination-selector:hover {
          border-color: var(--oracle-red);
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }

        .destination-title {
          font-size: 12px;
          font-weight: 600;
          color: var(--oracle-text-secondary);
          margin-bottom: 8px;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .destination-select {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid var(--oracle-border);
          border-radius: 6px;
          font-size: 11px;
          background: var(--input-bg);
          color: var(--text);
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .destination-select:focus {
          outline: none;
          border-color: var(--oracle-red);
          box-shadow: 0 0 0 1px rgba(72, 136, 224, 0.1);
          background: var(--card-bg);
        }

        .destination-help {
          margin-top: 8px;
          font-size: 10px;
          color: var(--oracle-text-muted);
          line-height: 1.3;
        }

        .destination-help strong {
          color: var(--oracle-text-secondary);
          font-weight: 500;
        }

        .files-content {
          flex: 1;
          overflow-y: auto;
          padding: 12px 16px;
        }

        .files-section {
          margin-bottom: 20px;
        }

        .section-title {
          font-size: 12px;
          font-weight: 600;
          color: var(--oracle-text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 10px;
          padding-left: 4px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .section-count {
          background: var(--input-bg);
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 10px;
          color: var(--text-secondary);
          font-weight: normal;
        }

        /* S-I — Layout grille : icone+info en haut, actions sur ligne séparée
           pour ne plus squeezer le titre quand 6 icônes sont rendues. */
        .file-item {
          display: grid;
          grid-template-columns: auto 1fr;
          grid-template-rows: auto auto;
          column-gap: 10px;
          row-gap: 6px;
          align-items: start;
          padding: 10px 12px;
          margin: 4px 0;
          background: var(--card-bg);
          border: 1px solid var(--oracle-border);
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          position: relative;
        }

        .file-item:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          border-color: #7c3aed;
        }

        .file-item.permanent {
          border-left: 2px solid #7c3aed;
        }

        .file-item.session {
          border-left: 2px solid #f59e0b;
        }

        .file-icon {
          font-size: 18px;
          width: 20px;
          text-align: center;
          flex-shrink: 0;
          grid-column: 1;
          grid-row: 1;
        }

        .file-info {
          min-width: 0;
          overflow: hidden;
          grid-column: 2;
          grid-row: 1;
        }

        .file-name {
          font-size: 12px;
          font-weight: 500;
          color: var(--oracle-text-primary);
          margin-bottom: 3px;
          word-break: break-word;
          line-height: 1.3;
        }

        .file-meta {
          font-size: 10px;
          color: var(--oracle-text-muted);
          margin-bottom: 5px;
        }

        .file-tags {
          display: flex;
          gap: 4px;
          flex-wrap: wrap;
        }

        .file-tag {
          font-size: 9px;
          padding: 2px 5px;
          border-radius: 4px;
          font-weight: 500;
          color: white;
          text-transform: uppercase;
        }

        /* Actions sur leur propre ligne (grid row 2) — toujours visibles
           dans la sidebar (vue compacte importante pour scan rapide). */
        .file-actions {
          grid-column: 1 / -1;
          grid-row: 2;
          display: flex;
          gap: 4px;
          flex-wrap: wrap;
          padding-top: 4px;
          border-top: 1px dashed var(--oracle-border);
          margin-top: 2px;
          justify-content: flex-end;
          opacity: 0.85;
          transition: opacity 0.15s ease;
        }

        .file-item:hover .file-actions {
          opacity: 1;
        }

        .action-btn {
          width: 26px;
          height: 26px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--input-bg);
          border: 1px solid var(--oracle-border);
          border-radius: 5px;
          cursor: pointer;
          transition: all 0.2s ease;
          color: var(--oracle-text-secondary);
        }

        .action-btn:hover {
          background: var(--oracle-red);
          color: white;
          transform: scale(1.05);
        }

        .status-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #16a34a;
          position: absolute;
          top: 6px;
          right: 6px;
        }

        .upload-zone {
          margin: 8px 16px;
          padding: 12px;
          border: 2px dashed var(--oracle-border);
          border-radius: 6px;
          text-align: center;
          background: var(--card-bg);
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .upload-zone.active {
          border-color: var(--oracle-red);
          background: rgba(72, 136, 224, 0.02);
        }

        .upload-zone:hover {
          border-color: var(--oracle-red);
          background: rgba(72, 136, 224, 0.01);
        }

        .upload-icon {
          font-size: 24px;
          color: var(--oracle-text-muted);
          margin-bottom: 6px;
        }

        .upload-text {
          font-size: 12px;
          color: var(--oracle-text-secondary);
          font-weight: 500;
          margin-bottom: 2px;
        }

        .upload-subtext {
          font-size: 10px;
          color: var(--oracle-text-muted);
        }

        .loading {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 32px;
          color: var(--text-secondary);
          font-size: 12px;
        }

        .no-files {
          text-align: center;
          padding: 32px 16px;
          color: var(--text-muted);
          font-size: 12px;
        }

        /* Section statistiques - informations supplémentaires mockup */
        .stats-section {
          margin: 12px 16px 16px;
          background: var(--card-bg);
          border: 1px solid var(--card-border);
          border-radius: 8px;
          overflow: hidden;
        }

        .stats-header {
          background: var(--input-bg);
          padding: 8px 12px;
          border-bottom: 1px solid var(--card-border);
          font-size: 12px;
          font-weight: 600;
          color: var(--text);
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .stat-item {
          padding: 10px 12px;
          border-bottom: 1px solid var(--card-border);
        }

        .stat-item:last-child {
          border-bottom: none;
        }

        .stat-label {
          font-size: 10px;
          color: var(--text-secondary);
          margin-bottom: 4px;
        }

        .stat-value {
          font-size: 11px;
          font-weight: 600;
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 4px;
        }

        .stat-highlight {
          color: #7c3aed;
        }

        .progress-bar {
          width: 100%;
          height: 4px;
          background: var(--input-border);
          border-radius: 2px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #7c3aed 0%, #f59e0b 100%);
          border-radius: 2px;
          transition: width 0.3s ease;
        }
      `}</style>

      {/* Header avec recherche et filtres */}
      <div className="files-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="files-title">Fichiers Projet</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <button
              onClick={handleExportZip}
              disabled={exportingZip}
              title="Télécharger tous les fichiers du projet en .zip"
              style={{
                background: 'none', border: '1px solid var(--card-border)',
                borderRadius: '5px', padding: '4px 6px',
                cursor: exportingZip ? 'wait' : 'pointer', opacity: exportingZip ? 0.6 : 1,
                color: 'var(--text-secondary)', display: 'flex', alignItems: 'center',
                transition: 'all 0.2s'
              }}
              onMouseEnter={e => { if (!exportingZip) { e.currentTarget.style.borderColor = '#7c3aed'; e.currentTarget.style.color = '#7c3aed'; } }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--card-border)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
            >
              <Download size={12} className={exportingZip ? 'spin' : ''} />
            </button>
            <button
              onClick={() => { loadFilesData(); }}
              title="Rafraîchir les fichiers"
              style={{
                background: 'none', border: '1px solid var(--card-border)',
                borderRadius: '5px', padding: '4px 6px', cursor: 'pointer',
                color: 'var(--text-secondary)', display: 'flex', alignItems: 'center',
                transition: 'all 0.2s'
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#7c3aed'; e.currentTarget.style.color = '#7c3aed'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--card-border)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
            >
              <RefreshCw size={12} />
            </button>
          </div>
        </div>

        <div className="search-box">
          <Search className="search-icon" size={12} />
          <input
            type="text"
            placeholder="Rechercher fichiers..."
            className="search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="filter-buttons">
          <button
            className={`filter-btn ${filterType === 'all' ? 'active' : ''}`}
            onClick={() => setFilterType('all')}
          >
            Tous
          </button>
          <button
            className={`filter-btn ${filterType === 'expertise' ? 'active' : ''}`}
            onClick={() => setFilterType('expertise')}
          >
            Expertise
          </button>
          <button
            className={`filter-btn ${filterType === 'cloud' ? 'active' : ''}`}
            onClick={() => setFilterType('cloud')}
          >
            ITRIIA Cloud
          </button>
          <button
            className={`filter-btn ${filterType === 'types' ? 'active' : ''}`}
            onClick={() => setFilterType('types')}
          >
            Types
          </button>
        </div>
      </div>

      {/* Phase A (11 avril 2026) — Sélecteur de destination retiré.
          Les uploads vont directement dans le projet courant.
          La V2 base d'expertise réintégrera un sélecteur enrichi
          (projet / base X / base Y / nouvelle base) quand elle sera prête. */}

      {/* Arborescence dossiers */}
      <FolderTree
        projectId={projectId}
        currentFolderId={currentFolderId}
        onFolderSelect={(folderId) => {
          setCurrentFolderId(folderId);
          // Charger le breadcrumb si un dossier est sélectionné
          if (folderId !== null) {
            const token = localStorage.getItem('oracle_token');
            axios.get(`/api/projects/${projectId}/folders/${folderId}`, {
              headers: { Authorization: `Bearer ${token}` }
            }).then(res => {
              if (res.data.success) setCurrentBreadcrumb(res.data.breadcrumb || []);
            }).catch(() => setCurrentBreadcrumb([]));
          } else {
            setCurrentBreadcrumb([]);
          }
        }}
        refreshKey={folderRefreshKey}
      />

      {/* Breadcrumb si dans un dossier */}
      {currentFolderId !== null && currentBreadcrumb.length > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          padding: '6px 16px',
          fontSize: 10,
          color: 'var(--text-secondary)',
          borderBottom: '1px solid var(--oracle-border)',
          flexWrap: 'wrap'
        }}>
          <FolderOpen size={11} style={{ color: '#a78bfa', flexShrink: 0 }} />
          <span
            onClick={() => { setCurrentFolderId(null); setCurrentBreadcrumb([]); }}
            style={{ cursor: 'pointer', color: '#a78bfa' }}
          >
            Racine
          </span>
          {currentBreadcrumb.map((crumb, i) => (
            <React.Fragment key={crumb.id}>
              <span style={{ color: 'var(--text-muted)' }}>/</span>
              <span
                onClick={() => {
                  setCurrentFolderId(crumb.id);
                  setCurrentBreadcrumb(currentBreadcrumb.slice(0, i + 1));
                }}
                style={{
                  cursor: i < currentBreadcrumb.length - 1 ? 'pointer' : 'default',
                  color: i < currentBreadcrumb.length - 1 ? '#a78bfa' : 'var(--text)',
                  fontWeight: i === currentBreadcrumb.length - 1 ? 600 : 400
                }}
              >
                {crumb.name}
              </span>
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Zone d'upload */}
      <div
        className={`upload-zone ${uploadZoneActive ? 'active' : ''}`}
        onClick={() => {
          const input = document.createElement('input');
          input.type = 'file';
          input.multiple = true;
          input.onchange = (e) => handleUpload(Array.from(e.target.files));
          input.click();
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setUploadZoneActive(true);
        }}
        onDragLeave={() => setUploadZoneActive(false)}
        onDrop={(e) => {
          e.preventDefault();
          setUploadZoneActive(false);
          handleUpload(Array.from(e.dataTransfer.files));
        }}
      >
        {isUploading ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
              <div style={{
                width: '16px', height: '16px', border: '2px solid var(--card-border)',
                borderTop: '2px solid #7c3aed', borderRadius: '50%',
                animation: 'spin 0.8s linear infinite'
              }} />
              <div className="upload-text" style={{ fontSize: '11px' }}>
                Upload de {uploadingNames.length} fichier{uploadingNames.length > 1 ? 's' : ''}...
              </div>
            </div>
            <div style={{ fontSize: '9px', color: 'var(--text-secondary)', marginTop: '4px', maxHeight: '40px', overflow: 'hidden' }}>
              {uploadingNames.slice(0, 3).join(', ')}{uploadingNames.length > 3 ? ` +${uploadingNames.length - 3}` : ''}
            </div>
          </>
        ) : (
          <>
            <div className="upload-icon">📤</div>
            <div className="upload-text">Glisser fichiers ici</div>
            <div className="upload-subtext">ou cliquer pour parcourir</div>
          </>
        )}
      </div>

      {/* Bouton Import ZIP */}
      <div style={{ margin: '0 16px 8px', display: 'flex', gap: 8 }}>
        <button
          onClick={() => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = '.zip';
            input.onchange = (e) => {
              if (e.target.files[0]) handleZipImport(e.target.files[0]);
            };
            input.click();
          }}
          disabled={zipImporting}
          style={{
            flex: 1,
            padding: '7px 12px',
            background: zipImporting ? 'var(--input-bg)' : 'transparent',
            border: '1px dashed var(--oracle-border)',
            borderRadius: 6,
            fontSize: 11,
            color: 'var(--text-secondary)',
            cursor: zipImporting ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            transition: 'all 0.2s'
          }}
          onMouseEnter={e => { if (!zipImporting) { e.currentTarget.style.borderColor = '#a78bfa'; e.currentTarget.style.color = '#a78bfa'; }}}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--oracle-border)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
        >
          <Archive size={13} />
          {zipImporting ? 'Import en cours...' : 'Importer un ZIP'}
        </button>
      </div>

      {/* Barre progression ZIP */}
      {zipImporting && zipProgress && (
        <div style={{
          margin: '0 16px 8px',
          padding: '8px 10px',
          background: 'rgba(167,139,250,0.08)',
          border: '1px solid rgba(167,139,250,0.2)',
          borderRadius: 6,
          fontSize: 10
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, color: 'var(--text)' }}>
            <span style={{ fontWeight: 600 }}>{zipProgress.label}</span>
            {zipProgress.total > 0 && (
              <span style={{ color: '#a78bfa' }}>{Math.round((zipProgress.current / zipProgress.total) * 100)}%</span>
            )}
          </div>
          {zipProgress.total > 0 && (
            <div style={{ width: '100%', height: 4, background: 'var(--input-border)', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: `${Math.round((zipProgress.current / zipProgress.total) * 100)}%`,
                background: 'linear-gradient(90deg, #a78bfa, #7c3aed)',
                borderRadius: 2,
                transition: 'width 0.3s ease'
              }} />
            </div>
          )}
        </div>
      )}

      {/* Bandeau filtre Famille contextuel */}
      {familyFilterActive && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
          padding: '8px 12px', margin: '0 12px 8px',
          background: 'linear-gradient(135deg, rgba(124,58,237,0.08) 0%, rgba(168,85,247,0.08) 100%)',
          border: '1px solid rgba(124,58,237,0.22)',
          borderRadius: 8,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
            <span style={{
              fontSize: 10, fontWeight: 700, color: '#7c3aed',
              letterSpacing: 0.5, textTransform: 'uppercase',
              padding: '2px 6px', background: 'rgba(124,58,237,0.12)',
              borderRadius: 4, flexShrink: 0,
            }}>Famille</span>
            <span style={{ fontSize: 11, color: 'var(--text-secondary)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {Object.keys(familyRoutingByFile).length} fichier{Object.keys(familyRoutingByFile).length > 1 ? 's' : ''} routé{Object.keys(familyRoutingByFile).length > 1 ? 's' : ''}
            </span>
          </div>
          <button
            onClick={() => setFamilyFilterDisabled(true)}
            title="Voir tous les fichiers du projet"
            style={{
              background: 'transparent', border: 'none',
              color: 'var(--text-secondary)', cursor: 'pointer',
              padding: 2, borderRadius: 4, display: 'flex', alignItems: 'center',
            }}
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Contenu fichiers */}
      <div className="files-content">
        {loading ? (
          <div className="loading">
            <Clock size={16} style={{ marginRight: '8px' }} />
            Chargement fichiers...
          </div>
        ) : (
          <>
            {/* Barre d'action multi-select */}
            {selectMode && selectedFileIds.size > 0 && (
              <div style={{
                padding: '8px 10px', marginBottom: '8px',
                background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.3)',
                borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                fontSize: '11px', gap: '6px'
              }}>
                <span style={{ color: 'var(--text)', fontWeight: 600 }}>
                  {selectedFileIds.size} fichier{selectedFileIds.size > 1 ? 's' : ''}
                </span>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button onClick={selectAll} style={{
                    padding: '3px 8px', borderRadius: '4px', border: '1px solid rgba(124,58,237,0.3)',
                    background: 'transparent', color: '#a78bfa', cursor: 'pointer', fontSize: '10px'
                  }}>Tout</button>
                  <button onClick={deselectAll} style={{
                    padding: '3px 8px', borderRadius: '4px', border: '1px solid rgba(124,58,237,0.3)',
                    background: 'transparent', color: '#a78bfa', cursor: 'pointer', fontSize: '10px'
                  }}>Aucun</button>
                  {canShare && (
                    <button onClick={() => openShareModal([...selectedFileIds])} style={{
                      padding: '3px 10px', borderRadius: '4px', border: 'none',
                      background: '#8b5cf6', color: '#fff', cursor: 'pointer', fontSize: '10px', fontWeight: 600,
                      display: 'flex', alignItems: 'center', gap: 3
                    }}><Share2 size={10} /> Partager</button>
                  )}
                  <button onClick={handleBatchAnalyze} style={{
                    padding: '3px 10px', borderRadius: '4px', border: 'none',
                    background: '#7c3aed', color: '#fff', cursor: 'pointer', fontSize: '10px', fontWeight: 600
                  }}>Analyser ({selectedFileIds.size})</button>
                </div>
              </div>
            )}

            {/* Fichiers Projet Permanent */}
            {filterFiles(permanentFiles).length > 0 && (
              <div className="files-section">
                <div className="section-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>📁 Permanent
                  <span className="section-count">{filterFiles(permanentFiles).length}</span></span>
                  <button onClick={() => { setSelectMode(prev => !prev); if (selectMode) deselectAll(); }}
                    style={{
                      padding: '2px 8px', borderRadius: '4px', border: '1px solid var(--card-border)',
                      background: selectMode ? 'rgba(124,58,237,0.15)' : 'transparent',
                      color: selectMode ? '#a78bfa' : 'var(--text-secondary)',
                      cursor: 'pointer', fontSize: '9px', fontWeight: 500
                    }}>
                    {selectMode ? '✕ Annuler' : '☐ Sélection'}
                  </button>
                </div>

                {filterFiles(permanentFiles).map((file) => (
                  <div key={file.id} className="file-item permanent"
                    onClick={selectMode ? () => toggleFileSelect(file.id) : undefined}
                    style={selectMode ? { cursor: 'pointer' } : {}}>
                    {selectMode && (
                      <div style={{
                        width: '16px', height: '16px', borderRadius: '3px', flexShrink: 0,
                        border: selectedFileIds.has(file.id) ? 'none' : '2px solid var(--text-secondary)',
                        background: selectedFileIds.has(file.id) ? '#7c3aed' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        marginRight: '6px', transition: 'all 0.15s'
                      }}>
                        {selectedFileIds.has(file.id) && <span style={{ color: '#fff', fontSize: '10px', fontWeight: 700 }}>✓</span>}
                      </div>
                    )}
                    <div className="file-icon">{file.icon}</div>
                    <div className="file-info">
                      <div className="file-name">{file.name}</div>
                      <div className="file-meta">
                        {file.size} • {file.age}
                        {file.status && <span style={{color: '#7c3aed'}}> • {file.status}</span>}
                      </div>
                      <div className="file-tags">
                        {file.tags.map((tag, i) => (
                          <span
                            key={i}
                            className="file-tag"
                            style={{ backgroundColor: getTagColor(tag) }}
                          >
                            {tag}
                          </span>
                        ))}
                        {familyRoutingByFile[file.id] && (
                          <DocCategoryChip
                            category={familyRoutingByFile[file.id].category}
                            nationalityTag={familyRoutingByFile[file.id].nationality_tag}
                          />
                        )}
                      </div>
                    </div>
                    <div className="status-dot"></div>
                    {/* Session F+1 P2 — refactor : <FileActionButtons> partagé (cohérence avec FilesInterface) */}
                    <div className="file-actions" onClick={(e) => e.stopPropagation()}>
                      <FileActionButtons
                        file={{ ...file, original_name: file.name }}
                        projectId={projectId}
                        canShare={canShare}
                        onPreview={(f) => setPreviewFile({ id: f.id, name: f.name || f.original_name || file.name })}
                        onAnalyze={(fid, fname) => onFileAnalyze && onFileAnalyze(fid, fname || file.name, 'analyze')}
                        onChanged={loadFilesData}
                        showCore={file.actions?.includes('delete') ?? true}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Fichiers Session Actuelle */}
            {filterFiles(sessionFiles).length > 0 && (
              <div className="files-section">
                <div className="section-title">
                  🕐 Fichiers Session Actuelle
                  <span className="section-count">{filterFiles(sessionFiles).length}</span>
                </div>

                {/* Banner lifecycle session */}
                <div style={{
                  padding: '8px 10px',
                  marginBottom: '8px',
                  background: 'rgba(245, 158, 11, 0.08)',
                  border: '1px solid rgba(245, 158, 11, 0.2)',
                  borderRadius: '6px',
                  fontSize: '10px',
                  color: 'var(--text-secondary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <span>Fichiers liés à cette session</span>
                  <button
                    onClick={() => {
                      if (confirm(`Supprimer les ${sessionFiles.length} fichiers de session ?`)) {
                        sessionFiles.forEach(f => handleDeleteFile(f.id));
                      }
                    }}
                    style={{
                      background: 'none',
                      border: '1px solid rgba(245, 158, 11, 0.4)',
                      borderRadius: '4px',
                      padding: '2px 8px',
                      fontSize: '9px',
                      color: '#f59e0b',
                      cursor: 'pointer'
                    }}
                  >
                    Tout supprimer
                  </button>
                </div>

                {filterFiles(sessionFiles).map((file) => (
                  <div key={file.id} className="file-item session"
                    onClick={selectMode ? () => toggleFileSelect(file.id) : undefined}
                    style={selectMode ? { cursor: 'pointer' } : {}}>
                    {selectMode && (
                      <div style={{
                        width: '16px', height: '16px', borderRadius: '3px', flexShrink: 0,
                        border: selectedFileIds.has(file.id) ? 'none' : '2px solid var(--text-secondary)',
                        background: selectedFileIds.has(file.id) ? '#7c3aed' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        marginRight: '6px', transition: 'all 0.15s'
                      }}>
                        {selectedFileIds.has(file.id) && <span style={{ color: '#fff', fontSize: '10px', fontWeight: 700 }}>✓</span>}
                      </div>
                    )}
                    <div className="file-icon">{file.icon}</div>
                    <div className="file-info">
                      <div className="file-name">{file.name}</div>
                      <div className="file-meta">{file.size} • {file.age}</div>
                      <div className="file-tags">
                        {file.tags.map((tag, i) => (
                          <span
                            key={i}
                            className="file-tag"
                            style={{ backgroundColor: getTagColor(tag) }}
                          >
                            {tag}
                          </span>
                        ))}
                        {familyRoutingByFile[file.id] && (
                          <DocCategoryChip
                            category={familyRoutingByFile[file.id].category}
                            nationalityTag={familyRoutingByFile[file.id].nationality_tag}
                          />
                        )}
                      </div>
                    </div>
                    <div className="file-actions">
                      {file.actions.map((action, i) => (
                        <button
                          key={i}
                          className="action-btn"
                          onClick={() => {
                            if (action === 'delete') handleDeleteFile(file.id);
                            else if (action === 'view') setPreviewFile({ id: file.id, name: file.name });
                            else if (action === 'analyze' && onFileAnalyze) onFileAnalyze(file.id, file.name, 'analyze');
                          }}
                          title={action}
                        >
                          {getActionIcon(action)}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Aucun fichier */}
            {filterFiles(permanentFiles).length === 0 && filterFiles(sessionFiles).length === 0 && !loading && (
              <div className="no-files" style={accessError ? { color: '#ef4444' } : undefined}>
                <FileCheck size={32} style={{ opacity: 0.3, marginBottom: '8px' }} />
                <div>{accessError || 'Aucun fichier trouvé'}</div>
                {!accessError && (
                  <div style={{ fontSize: '10px', marginTop: '4px' }}>
                    Uploadez des fichiers ou ajustez vos filtres
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Coller en fichier */}
      {pasteWidget}

      {/* Section statistiques - NOUVELLES STATISTIQUES RÉELLES */}
      <div className="stats-section">
        <div className="stats-header">
          <BarChart3 size={12} />
          Statistiques Projet
        </div>

        <div className="stat-item">
          <div className="stat-label">Usage Espace</div>
          <div className="stat-value">
            <span>Utilisé</span>
            <span>{stats.usedSpace} / {stats.totalSpace}</span>
          </div>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${stats.usagePercentage}%` }}></div>
          </div>
        </div>

        <div className="stat-item">
          <div className="stat-label">Dernière activité</div>
          <div className="stat-value">
            <span>Upload</span>
            <span>{stats.lastSync}</span>
          </div>
        </div>
      </div>

      {/* Modal Renommer fichier (Session E — P-FILE-ACTIONS) */}
      {renameFileId !== null && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
            zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
          onClick={() => !renaming && setRenameFileId(null)}
        >
          <div
            style={{
              background: 'var(--card-bg)', border: '1px solid var(--oracle-border)',
              borderRadius: 10, padding: 20, minWidth: 380, maxWidth: 480,
              boxShadow: '0 20px 50px rgba(0,0,0,0.4)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <Edit3 size={16} style={{ color: '#a78bfa' }} />
              <h3 style={{ margin: 0, fontSize: 14, color: 'var(--text)' }}>Renommer le fichier</h3>
            </div>
            <input
              type="text"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && renameValue.trim()) handleRenameFile();
                if (e.key === 'Escape' && !renaming) setRenameFileId(null);
              }}
              autoFocus
              disabled={renaming}
              maxLength={200}
              style={{
                width: '100%', padding: '9px 12px', fontSize: 13, color: 'var(--text)',
                background: 'var(--input-bg, var(--card-bg))', border: '1px solid var(--oracle-border)',
                borderRadius: 6, outline: 'none', boxSizing: 'border-box',
                marginBottom: 14
              }}
              placeholder="Nouveau nom"
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setRenameFileId(null)}
                disabled={renaming}
                style={{
                  padding: '7px 14px', fontSize: 12, color: 'var(--text-secondary)',
                  background: 'transparent', border: '1px solid var(--oracle-border)',
                  borderRadius: 5, cursor: renaming ? 'not-allowed' : 'pointer'
                }}
              >
                Annuler
              </button>
              <button
                onClick={handleRenameFile}
                disabled={renaming || !renameValue.trim()}
                style={{
                  padding: '7px 14px', fontSize: 12, color: '#fff',
                  background: renaming || !renameValue.trim() ? 'var(--oracle-border)' : '#7c3aed',
                  border: 'none', borderRadius: 5,
                  cursor: renaming || !renameValue.trim() ? 'not-allowed' : 'pointer',
                  fontWeight: 500
                }}
              >
                {renaming ? 'Renommage…' : 'Renommer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Preview */}
      {/* Menu déplacer fichier — position fixed pour éviter le clip de overflow */}
      {moveFileId && moveMenuPos && (
        <div
          style={{
            position: 'fixed',
            top: moveMenuPos.top,
            left: moveMenuPos.left,
            zIndex: 9999,
            background: 'var(--card-bg)',
            border: '1px solid var(--oracle-border)',
            borderRadius: 6,
            boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
            minWidth: 200,
            maxHeight: 240,
            overflowY: 'auto',
            padding: '4px 0',
            fontSize: 11
          }}
          onClick={e => e.stopPropagation()}
        >
          <div style={{
            padding: '6px 10px', fontWeight: 600, color: 'var(--text-secondary)',
            borderBottom: '1px solid var(--oracle-border)', fontSize: 10, textTransform: 'uppercase'
          }}>
            Déplacer vers
          </div>
          <button
            onClick={() => handleMoveFile(moveFileId, null)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, width: '100%', padding: '6px 10px',
              border: 'none', background: 'transparent', color: 'var(--text)', fontSize: 11, cursor: 'pointer', textAlign: 'left'
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--card-hover)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <FolderOpen size={12} style={{ color: '#6b7280' }} /> Racine
          </button>
          {foldersList.map(f => (
            <button
              key={f.id}
              onClick={() => handleMoveFile(moveFileId, f.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, width: '100%', padding: '6px 10px',
                border: 'none', background: 'transparent', color: 'var(--text)', fontSize: 11, cursor: 'pointer', textAlign: 'left'
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--card-hover)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <FolderOpen size={12} style={{ color: '#a78bfa' }} /> {f.displayName}
            </button>
          ))}
          {foldersList.length === 0 && (
            <div style={{ padding: '8px 10px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
              Aucun dossier
            </div>
          )}
          <div style={{ borderTop: '1px solid var(--oracle-border)', margin: '4px 0' }} />
          <button
            onClick={() => setMoveFileId(null)}
            style={{
              width: '100%', padding: '5px 10px', border: 'none', background: 'transparent',
              color: 'var(--text-muted)', fontSize: 10, cursor: 'pointer', textAlign: 'center'
            }}
          >
            Annuler
          </button>
        </div>
      )}

      {previewFile && (
        <FilePreviewModal
          projectId={projectId}
          fileId={previewFile.id}
          fileName={previewFile.name}
          onClose={() => setPreviewFile(null)}
          onAnalyze={(fId, fName, mode) => {
            setPreviewFile(null);
            if (onFileAnalyze) onFileAnalyze(fId, fName, mode);
          }}
        />
      )}

      {/* Modal partage cross-projet (Session 31) */}
      {shareModalOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 10000,
          background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }} onClick={() => setShareModalOpen(false)}>
          <div style={{
            background: 'var(--card-bg)', border: '1px solid var(--card-border)',
            borderRadius: 12, padding: 24, width: 380, maxWidth: '90vw',
            boxShadow: '0 16px 48px rgba(0,0,0,0.3)'
          }} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
                <Share2 size={16} style={{ color: '#8b5cf6' }} />
                Partager {shareFileIds.length} fichier{shareFileIds.length > 1 ? 's' : ''}
              </div>
              <button onClick={() => setShareModalOpen(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex' }}>
                <X size={16} />
              </button>
            </div>

            {/* Projet cible */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
                Projet cible
              </label>
              <select value={shareTargetProject}
                onChange={e => { setShareTargetProject(e.target.value); setShareTargetFolder(''); loadShareTargetFolders(e.target.value); }}
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: 8,
                  border: '1px solid var(--card-border)', background: 'var(--input-bg)',
                  color: 'var(--text)', fontSize: 13
                }}>
                <option value="">Sélectionner un projet...</option>
                {shareProjects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            {/* Dossier cible */}
            {shareTargetProject && shareTargetFolders.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
                  Dossier (optionnel)
                </label>
                <select value={shareTargetFolder} onChange={e => setShareTargetFolder(e.target.value)}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: 8,
                    border: '1px solid var(--card-border)', background: 'var(--input-bg)',
                    color: 'var(--text)', fontSize: 13
                  }}>
                  <option value="">Racine du projet</option>
                  {shareTargetFolders.map(f => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Info */}
            <div style={{
              padding: '8px 12px', background: 'rgba(139,92,246,0.06)',
              border: '1px solid rgba(139,92,246,0.15)', borderRadius: 6,
              fontSize: 11, color: 'var(--text-secondary)', marginBottom: 16, lineHeight: 1.5
            }}>
              Les fichiers seront copiés intégralement (contenu, métadonnées, indexation Weaviate).
              Le fichier source reste inchangé.
            </div>

            {/* Bouton */}
            <button onClick={handleShare} disabled={!shareTargetProject || sharing}
              style={{
                width: '100%', padding: 12, borderRadius: 8, border: 'none',
                background: shareTargetProject && !sharing ? 'linear-gradient(135deg, #8b5cf6, #7c3aed)' : 'var(--card-border)',
                color: 'white', fontSize: 13, fontWeight: 600,
                cursor: shareTargetProject && !sharing ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
              }}>
              <Share2 size={14} />
              {sharing ? 'Partage en cours...' : 'Partager'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilesSidebar;
